import React from "react";
import { RouteRecord } from "vite-react-ssg";
import { redirectMapping } from "./utils/redirects";

// Critical route - load immediately
import { Homepage } from "./pages/Homepage";

// Helper to create lazy-loaded routes using the data router API (route.lazy).
// vite-react-ssg pre-resolves route.lazy before hydration, preventing the
// flash that React.lazy() + Suspense causes on hard refresh.
// Constrain the SELECTED export (m[exportName]) to a prop-less component the router can render
// directly — rather than requiring EVERY export in the module to be prop-less. This lets a module
// additionally export a props-bearing component (e.g. the generic TechDetailPage, routed via its
// prop-less TechWebbloqsPage/TechMagellanPage wrappers) without breaking the constraint. The
// runtime contract is unchanged: exportName must still name a component rendered without props.
const lazyRoute = <K extends string, T extends Record<K, React.ComponentType>>(
    path: string,
    importFn: () => Promise<T>,
    exportName: K
): RouteRecord => ({
    path,
    lazy: () =>
        importFn().then(m => {
            const Component = m[exportName];
            if (process.env.NODE_ENV !== "production" && !Component) {
                throw new Error(
                    `lazyRoute("${path}"): "${exportName}" not found. Exports: ${Object.keys(m).join(", ")}`
                );
            }
            return { Component };
        }),
});

// Generate redirect routes from redirectMapping
// These must be placed before dynamic routes (like /blog/:slug) to ensure redirects are matched first
const redirectRoutes: RouteRecord[] = redirectMapping
    ? Object.keys(redirectMapping).map(path => lazyRoute(path, () => import("./pages/Redirect"), "Redirect"))
    : [];

// Define routes for vite-react-ssg
export const routes: RouteRecord[] = [
    // Homepage
    {
        path: "/",
        Component: () => <Homepage heroHighlightSecondLine />,
    },
    {
        path: "/en",
        Component: () => <Homepage heroHighlightSecondLine />,
    },
    // Redirect routes - must be before dynamic routes to ensure redirects are matched first
    ...redirectRoutes,
    // Blog routes
    lazyRoute("/blog", () => import("./pages/BlogPage"), "BlogPage"),
    lazyRoute("/blog/:slug", () => import("./pages/BlogPostPage"), "BlogPostPage"),
    lazyRoute("/en/blog", () => import("./pages/BlogPage"), "BlogPage"),
    lazyRoute("/en/blog/:slug", () => import("./pages/BlogPostPage"), "BlogPostPage"),
    // Loesungs-Seiten (/loesungen/*) und Branchen-Seiten (/branchen/:slug) abgebaut
    // 11.8.2026: Substanz mit Eckpfeiler 2 nach /plattform gewandert, Seiten selbst
    // seit dem Relaunch unverlinkt. Alle URLs 301en via redirectMapping.
    // Nicht zu verwechseln mit /angebote/branche/:slug — den fuenf neu getexteten
    // Branchenseiten am Offerten-Generator, die weiterleben.
    lazyRoute("/smart-client-relations", () => import("./pages/SmartClientRelationsPage"), "SmartClientRelationsPage"),
    lazyRoute(
        "/en/smart-client-relations",
        () => import("./pages/SmartClientRelationsPage"),
        "SmartClientRelationsPage"
    ),
    // Note: /produkte/enterprise and /produkte/best now redirect via redirectMapping
    // Note: /vertriebsportal and /vertriebsportal-enterprise redirects are handled by redirectMapping
    // Module pages
    lazyRoute("/module/cpq", () => import("./pages/ModuleCpq"), "ModuleCpq"),
    lazyRoute("/en/modules/cpq", () => import("./pages/ModuleCpq"), "ModuleCpq"),
    // Der CPQ-Playground unter dem Modul. Er existierte als VpStarterPlaygroundPage,
    // bis der Offerten-Generator-Umbau (68f422ac) sie entfernte; hier ist er ohne den
    // abgebauten VP-Rahmen zurueck.
    lazyRoute("/module/cpq/playground", () => import("./pages/CpqPlaygroundPage"), "CpqPlaygroundPage"),
    lazyRoute("/en/modules/cpq/playground", () => import("./pages/CpqPlaygroundPage"), "CpqPlaygroundPage"),
    // Der Dienstleistungs-Katalog, ebenfalls unter dem CPQ-Modul. Komponente, Schema
    // und MDX lagen seit dem VP-Teardown unrouted im Repo (Kommentar unten); die alten
    // VP-URLs behalten ihren 301 auf /angebote, die Seite bekommt eine neue Adresse.
    lazyRoute("/module/cpq/katalog", () => import("./pages/ServiceCatalogPage"), "ServiceCatalogPage"),
    lazyRoute("/en/modules/cpq/catalogue", () => import("./pages/ServiceCatalogPage"), "ServiceCatalogPage"),
    // Die Detailseite je Dienst (Befund Jan, 13.8.2026). Sie blieb beim Umzug der
    // Katalogseite zurueck: Der VP-Teardown entfernte ihre Route
    // `/vertriebsportal/dienstleistungs-katalog/:serviceId`, `ServiceCatalogPage`
    // baute ihre Kartenlinks aber weiter darauf. Das Mapping faengt diesen Pfad nur
    // OHNE Unterpfad — jede Karte des Katalogs zeigte damit auf 404.
    //
    // Dieselbe Bauart wie `/module/cpq/playground`: dasselbe `PlaygroundEmbed`,
    // nur waehlt hier `serviceId` aus der URL den Konfigurator statt eines fest
    // verdrahteten Dienstes.
    lazyRoute(
        "/module/cpq/katalog/:serviceId",
        () => import("./pages/ServiceCatalogPreviewPage"),
        "ServiceCatalogPreviewPage"
    ),
    lazyRoute(
        "/en/modules/cpq/catalogue/:serviceId",
        () => import("./pages/ServiceCatalogPreviewPage"),
        "ServiceCatalogPreviewPage"
    ),
    lazyRoute("/module/appointments", () => import("./pages/ModuleAppointments"), "ModuleAppointments"),
    lazyRoute("/en/modules/appointments", () => import("./pages/ModuleAppointments"), "ModuleAppointments"),
    lazyRoute("/module/messaging", () => import("./pages/ModuleMessaging"), "ModuleMessaging"),
    lazyRoute("/en/modules/messaging", () => import("./pages/ModuleMessaging"), "ModuleMessaging"),
    lazyRoute("/module/rechnungsstellung", () => import("./pages/ModuleRechnungsstellung"), "ModuleRechnungsstellung"),
    lazyRoute("/en/modules/invoicing", () => import("./pages/ModuleRechnungsstellung"), "ModuleRechnungsstellung"),
    lazyRoute("/module/analytics", () => import("./pages/ModuleAnalytics"), "ModuleAnalytics"),
    lazyRoute("/en/modules/analytics", () => import("./pages/ModuleAnalytics"), "ModuleAnalytics"),
    // Angebote — the Eckpfeiler-1 catalog/distributor overview (Phase-3 Etappe 5c). Nullbau,
    // decided § 5 structure. EN slug /en/offerings (parent slug), maps segment-wise (Story D2).
    lazyRoute("/angebote", () => import("./pages/AngebotePage"), "AngebotePage"),
    lazyRoute("/en/offerings", () => import("./pages/AngebotePage"), "AngebotePage"),
    // Technologie — das CDS-Technologie-Schaufenster (Etappe 1). Eine vierte Nav-Top-Level-
    // Kategorie + Übersichtsseite im Spektrum-Muster (sechs Technologien als Felder). EN slug
    // /en/technology, Slug-Mapping technologie:{de,en} in url-slug-mapping.ts.
    lazyRoute("/technologie", () => import("./pages/TechnologiePage"), "TechnologiePage"),
    lazyRoute("/en/technology", () => import("./pages/TechnologiePage"), "TechnologiePage"),

    // Technologie-Detailseiten (Technologie-Schaufenster) — sie teilen sich den generischen
    // TechDetailPage-Renderer; je Seite bindet ein dünner prop-loser Wrapper-Export
    // (TechWebbloqsPage/TechMagellanPage) seinen contentKey, da lazyRoute keine Props durchreicht.
    // Der Marken-Slug ist in DE und EN identisch (Slug-Mapping webbloqs/magellan:{de,en}).
    // webbloqs (Etappe 2) — die erste Detailseite der IA /technologie/<name>.
    lazyRoute("/technologie/webbloqs", () => import("./pages/TechDetailPage"), "TechWebbloqsPage"),
    lazyRoute("/en/technology/webbloqs", () => import("./pages/TechDetailPage"), "TechWebbloqsPage"),
    // Magellan (Etappe 3) — die erste echt quelloffene Detailseite (`repo`+`developerCta` gesetzt).
    lazyRoute("/technologie/magellan", () => import("./pages/TechDetailPage"), "TechMagellanPage"),
    lazyRoute("/en/technology/magellan", () => import("./pages/TechDetailPage"), "TechMagellanPage"),
    // Agent-Skills (Etappe 4) — schlanke OSS-Detailseite (`repo`+`developerCta` gesetzt, keine
    // merkmale/nutzen). Marken-Slug `agent-skills` in DE und EN identisch.
    lazyRoute("/technologie/agent-skills", () => import("./pages/TechDetailPage"), "TechAgentSkillsPage"),
    lazyRoute("/en/technology/agent-skills", () => import("./pages/TechDetailPage"), "TechAgentSkillsPage"),
    // Plot.pm (Etappe 4) — volle OSS-Detailseite wie Magellan (mit Eigennutzung). Marken-Slug
    // `plot` in DE und EN identisch.
    lazyRoute("/technologie/plot", () => import("./pages/TechDetailPage"), "TechPlotPage"),
    lazyRoute("/en/technology/plot", () => import("./pages/TechDetailPage"), "TechPlotPage"),
    // AppShell (Etappe 5) — die letzte Detailseite, der zweite `repo`-leer-Fall (nach webbloqs):
    // das qs-app-shell-Repo ist heute privat → kein `repo`/`developerCta`/Hero-CTA. Marken-Slug
    // `appshell` in DE und EN identisch.
    lazyRoute("/technologie/appshell", () => import("./pages/TechDetailPage"), "TechAppShellPage"),
    lazyRoute("/en/technology/appshell", () => import("./pages/TechDetailPage"), "TechAppShellPage"),
    // websmith (Nachzügler zum Schaufenster) — repo-behafteter OSS-Fall wie Magellan
    // (`repo`+`developerCta` gesetzt, Hero-CTA extern aufs Repo). Marken-Slug `websmith` in
    // DE und EN identisch.
    lazyRoute("/technologie/websmith", () => import("./pages/TechDetailPage"), "TechWebsmithPage"),
    lazyRoute("/en/technology/websmith", () => import("./pages/TechDetailPage"), "TechWebsmithPage"),
    // Angebote — System Engineering (Relaunch Phase 3). The re-narrated successor to the
    // retired /angebote/individualsoftware (which now 301s here via redirectMapping). EN
    // page shipped in Etappe 3 (#501); `system-engineering` is a technical term, so the EN
    // slug is identical (segment-wise, Story D2), same page component.
    lazyRoute("/angebote/system-engineering", () => import("./pages/SystemEngineeringPage"), "SystemEngineeringPage"),
    lazyRoute(
        "/en/offerings/system-engineering",
        () => import("./pages/SystemEngineeringPage"),
        "SystemEngineeringPage"
    ),
    // Angebote — Feasibility Workshop (Relaunch Phase 3, Etappe 2b). The first of four
    // workshop detail pages; the shared page-workshop schema + FeasibilityWorkshopPage
    // component are reused by the next three. `feasibility-workshop` is a technical term,
    // so the EN slug is identical (segment-wise, Story D2), same page component.
    lazyRoute(
        "/angebote/feasibility-workshop",
        () => import("./pages/FeasibilityWorkshopPage"),
        "FeasibilityWorkshopPage"
    ),
    lazyRoute(
        "/en/offerings/feasibility-workshop",
        () => import("./pages/FeasibilityWorkshopPage"),
        "FeasibilityWorkshopPage"
    ),
    // Angebote — Mission Analysis (Relaunch Phase 3). The second of four workshop detail
    // pages; reuses the shared page-workshop schema with its own thin MissionAnalysisPage
    // component. `mission-analysis` is a technical term, so the EN slug is identical
    // (segment-wise, Story D2), same page component.
    lazyRoute("/angebote/mission-analysis", () => import("./pages/MissionAnalysisPage"), "MissionAnalysisPage"),
    lazyRoute("/en/offerings/mission-analysis", () => import("./pages/MissionAnalysisPage"), "MissionAnalysisPage"),
    // Angebote — Go-to-Market Test (Relaunch Phase 3). The third of four workshop detail
    // pages; reuses the shared page-workshop schema with its own thin GoToMarketTestPage
    // component. `go-to-market-test` is a technical term, so the EN slug is identical
    // (segment-wise, Story D2), same page component.
    lazyRoute("/angebote/go-to-market-test", () => import("./pages/GoToMarketTestPage"), "GoToMarketTestPage"),
    lazyRoute("/en/offerings/go-to-market-test", () => import("./pages/GoToMarketTestPage"), "GoToMarketTestPage"),
    // Angebote — Product Scale-Up (Relaunch Phase 3). The fourth and last of four workshop
    // detail pages; reuses the shared page-workshop schema with its own thin ProductScaleUpPage
    // component. `product-scale-up` is a technical term, so the EN slug is identical
    // (segment-wise, Story D2), same page component.
    lazyRoute("/angebote/product-scale-up", () => import("./pages/ProductScaleUpPage"), "ProductScaleUpPage"),
    lazyRoute("/en/offerings/product-scale-up", () => import("./pages/ProductScaleUpPage"), "ProductScaleUpPage"),
    // Angebote — Product-Market Test. Die Uebersicht ueber die zwei Stufen darueber:
    // Sie erklaert, was ein Product-Market Test ist, und stellt in ihrer Weiche die
    // Frage, welche Stufe passt. Nimmt selbst KEINE Anfrage entgegen (Entscheid Jan,
    // 6.8.2026) — deshalb ohne `inquiryType`. `product-market-test` ist ein
    // Fachbegriff, der EN-Slug ist identisch (segmentweise, Story D2).
    lazyRoute("/angebote/product-market-test", () => import("./pages/ProductMarketTestPage"), "ProductMarketTestPage"),
    lazyRoute(
        "/en/offerings/product-market-test",
        () => import("./pages/ProductMarketTestPage"),
        "ProductMarketTestPage"
    ),
    // Angebote — Branchen-Offerten (§8 Hybrid-B VP rehome, Etappe 1). The 5 de-branded
    // industry-vertical offer pages, slug-driven by BranchePage. The old VP
    // /produkte/vertriebsportal/branche/:slug routes were removed in Etappe 2 and now
    // 301 slug-zu-slug hierher (via redirectMapping). EN path maps segment-wise
    // (angebote→offerings, branche→industry); the vertical SLUGS are per-slug-translated
    // by branchSlugMapping (e.g. gebaeudetechnik→building-technology, only facility-services
    // is identical). That table is shared, not VP-exclusive despite its name.
    lazyRoute("/angebote/branche/:slug", () => import("./pages/BranchePage"), "BranchePage"),
    lazyRoute("/en/offerings/industry/:slug", () => import("./pages/BranchePage"), "BranchePage"),
    // Angebote — Agentic SEO (Phase-3 Etappe 5b), the second offer detail page. Nullbau.
    // EN slug identical (technical term), maps segment-wise (Story D2).
    lazyRoute("/angebote/agentic-seo", () => import("./pages/AgenticSeoPage"), "AgenticSeoPage"),
    lazyRoute("/en/offerings/agentic-seo", () => import("./pages/AgenticSeoPage"), "AgenticSeoPage"),
    // /fuer-agenten — the Agentic-SEO demonstration page (proof for the offer above),
    // a top-level slug (not an /angebote child). EN slug for-agents, see Story D2.
    lazyRoute("/fuer-agenten", () => import("./pages/FuerAgentenPage"), "FuerAgentenPage"),
    lazyRoute("/en/for-agents", () => import("./pages/FuerAgentenPage"), "FuerAgentenPage"),
    // /agentic-development — the methodology page «Wie wir arbeiten» (Phase-3). Neubau,
    // top-level slug. EN slug identical (technical term), maps segment-wise (Story D2).
    lazyRoute("/agentic-development", () => import("./pages/AgenticDevelopmentPage"), "AgenticDevelopmentPage"),
    lazyRoute("/en/agentic-development", () => import("./pages/AgenticDevelopmentPage"), "AgenticDevelopmentPage"),
    // AI Potential Analysis — migrated from /dienstleistungen/ki-potenzialanalyse to the
    // /angebote catalog (Phase-3 Etappe 5d). Same page, new URL; the two old URLs redirect
    // via redirectMapping. EN slug unchanged (ai-potential-analysis), new parent offerings.
    lazyRoute(
        "/angebote/ki-potenzialanalyse",
        () => import("./pages/ServiceAiPotentialAnalysis"),
        "ServiceAiPotentialAnalysis"
    ),
    lazyRoute(
        "/en/offerings/ai-potential-analysis",
        () => import("./pages/ServiceAiPotentialAnalysis"),
        "ServiceAiPotentialAnalysis"
    ),
    // AI Agents Workshop — migrated from /dienstleistungen/ki-agenten-workshop (Etappe 5e).
    lazyRoute(
        "/angebote/ki-agenten-workshop",
        () => import("./pages/ServiceAiAgentsWorkshop"),
        "ServiceAiAgentsWorkshop"
    ),
    lazyRoute(
        "/en/offerings/ai-agent-workshop",
        () => import("./pages/ServiceAiAgentsWorkshop"),
        "ServiceAiAgentsWorkshop"
    ),
    // AI Technology landing page
    lazyRoute("/dienstleistungen/ki-technologie", () => import("./pages/ServiceAiTechnology"), "ServiceAiTechnology"),
    // Die beiden Beratungsseiten unter /ki-anwendungen (11.9.2026). Sie rahmen den
    // Katalog auf beiden Seiten: «potenzial» fragt vor dem Bauen «lohnt es sich?»,
    // «risiken» nach dem Bauen «was kann schiefgehen?». Das Elternsegment traegt
    // das «KI», deshalb heissen die Kinder kurz. Die alten URLs
    // /dienstleistungen/ki-risiken und /ki-anwendungen/ki-potenzial sowie die
    // kurzen /ki-risiken und /ki-potenzial 301en hierher (redirects.ts).
    //
    // Seit dem 18.9.2026 liegt das Elternsegment unter /angebote (Entscheid Jan);
    // die alten /ki-anwendungen/* 301en mit.
    lazyRoute("/angebote/ki-anwendungen/risiken", () => import("./pages/KiRisikenPage"), "KiRisikenPage"),
    lazyRoute("/angebote/ki-anwendungen/potenzial", () => import("./pages/KiPotenzialPage"), "KiPotenzialPage"),
    lazyRoute("/en/services/ai-technology", () => import("./pages/ServiceAiTechnology"), "ServiceAiTechnology"),
    lazyRoute("/en/offerings/ai-applications/risks", () => import("./pages/KiRisikenPage"), "KiRisikenPage"),
    lazyRoute("/en/offerings/ai-applications/potential", () => import("./pages/KiPotenzialPage"), "KiPotenzialPage"),
    // Kampagnen-Landeseiten unter /umfrage-unternehmen/ — Ziele der
    // reply.io-Sequenzen, absichtlich aus keiner Navigation verlinkt (Eintraege in
    // `routen-erreichbar.spec.ts`), aber indexierbar. Das erste Segment ist ein
    // reiner Sammelpfad: Es gibt keine Seite unter `/umfrage-unternehmen` selbst.
    lazyRoute("/umfrage-unternehmen/ergebnisse", () => import("./pages/SurveySignupPage"), "SurveySignupPage"),
    lazyRoute("/en/company-survey/results", () => import("./pages/SurveySignupPage"), "SurveySignupPage"),
    // Kampagnen-Landeseiten unter /kampagne/ (QUAWEB-40, Entscheid Jan 15.10.2026).
    // Sie gehoeren zu Mailkampagnen, nicht zur Angebotsstruktur, und stehen in
    // keiner Navigation (Praefix `/kampagne/` in `OHNE_WEG_GEWOLLT_PRAEFIX` von
    // `routen-erreichbar.spec.ts`). `/kampagne` und `/en/campaign` selbst sind
    // reine Sammelpfade ohne eigene Seite. Jede Seite ist eine Huelle um die
    // Vorlage `templates/CampaignPage.tsx`.
    //
    // Ausschreibungen: lag vom 23.9. bis 15.10.2026 unter
    // /angebote/ki-anwendungen/ausschreibungen (EN /en/offerings/ai-applications/tenders);
    // beide alten URLs 301en mit Query-String hierher (`redirects.ts`), weil die
    // Mailsequenz rfp-2026-09 noch laeuft. Die noch aeltere URL
    // /umfrage-unternehmen/ausschreibungen antwortet seit dem 23.9.2026 mit 404,
    // ausdruecklich entschieden.
    //
    // Die MDX-`id` bleibt `umfrage-unternehmen-ausschreibungen`: Sie traegt den
    // Inhalts-Schluessel (`umfrageUnternehmenAusschreibungen` in RfpPage), nicht die
    // URL. Sie umzubenennen liesse die Seite leer rendern — velite und typecheck
    // melden das nicht.
    lazyRoute("/kampagne/ausschreibungen", () => import("./pages/RfpPage"), "RfpPage"),
    lazyRoute("/en/campaign/tenders", () => import("./pages/RfpPage"), "RfpPage"),
    // KI-Potenzialanalyse und Systemintegration: nur deutsch, ohne EN-Route.
    // Beide Pfade stehen in `utils/german-only-paths.ts` (kein EN-Zwilling im
    // SSG, Umschalter auf /en/, kein hreflang="en") und in `NUR_DE_ERLAUBT`
    // (`App.routes.spec.ts`).
    lazyRoute(
        "/kampagne/ki-potenzialanalyse",
        () => import("./pages/KampagneKiPotenzialanalysePage"),
        "KampagneKiPotenzialanalysePage"
    ),
    lazyRoute(
        "/kampagne/systemintegration",
        () => import("./pages/KampagneSystemintegrationPage"),
        "KampagneSystemintegrationPage"
    ),
    // /kunden + /en/customers abgebaut (12.8.2026, Register 28): 301 auf
    // /referenzen. Entry 18 hatte «/kunden bleibt (additiv)» beschlossen — damals
    // ERGÄNZTE /referenzen. Seit 11.8. führt es ALLE Kunden in einer Liste
    // (Entry 23), damit wurde aus der Ergänzung eine Ablösung.
    //
    // /referenzen — the Zwei-Ebenen-Beweismodell page (Phase-3, § 5 Z. 171). EN /en/references
    // maps segment-wise (referenzen → references, url-slug-mapping).
    lazyRoute("/referenzen", () => import("./pages/ReferenzenPage"), "ReferenzenPage"),
    lazyRoute("/en/references", () => import("./pages/ReferenzenPage"), "ReferenzenPage"),
    lazyRoute("/success-stories/ewz", () => import("./pages/SuccessStoryEwz"), "SuccessStoryEwz"),
    lazyRoute("/en/success-stories/ewz", () => import("./pages/SuccessStoryEwz"), "SuccessStoryEwz"),
    lazyRoute(
        "/success-stories/meteoschweiz",
        () => import("./pages/SuccessStoryMeteoschweiz"),
        "SuccessStoryMeteoschweiz"
    ),
    lazyRoute(
        "/en/success-stories/meteoschweiz",
        () => import("./pages/SuccessStoryMeteoschweiz"),
        "SuccessStoryMeteoschweiz"
    ),
    lazyRoute("/success-stories/far", () => import("./pages/SuccessStoryFar"), "SuccessStoryFar"),
    lazyRoute("/en/success-stories/far", () => import("./pages/SuccessStoryFar"), "SuccessStoryFar"),
    lazyRoute("/success-stories/wvz", () => import("./pages/SuccessStoryWvz"), "SuccessStoryWvz"),
    lazyRoute("/en/success-stories/wvz", () => import("./pages/SuccessStoryWvz"), "SuccessStoryWvz"),
    lazyRoute(
        "/success-stories/liechtensteinische-post",
        () => import("./pages/SuccessStoryLiechtensteinischePost"),
        "SuccessStoryLiechtensteinischePost"
    ),
    lazyRoute(
        "/en/success-stories/liechtensteinische-post",
        () => import("./pages/SuccessStoryLiechtensteinischePost"),
        "SuccessStoryLiechtensteinischePost"
    ),
    lazyRoute(
        "/success-stories/naturgefahrenportal",
        () => import("./pages/SuccessStoryNaturgefahrenportal"),
        "SuccessStoryNaturgefahrenportal"
    ),
    lazyRoute(
        "/en/success-stories/naturgefahrenportal",
        () => import("./pages/SuccessStoryNaturgefahrenportal"),
        "SuccessStoryNaturgefahrenportal"
    ),
    lazyRoute(
        "/success-stories/ewz-fernwaerme",
        () => import("./pages/SuccessStoryEwzFernwaerme"),
        "SuccessStoryEwzFernwaerme"
    ),
    lazyRoute(
        "/en/success-stories/ewz-fernwaerme",
        () => import("./pages/SuccessStoryEwzFernwaerme"),
        "SuccessStoryEwzFernwaerme"
    ),
    // About Us pages
    lazyRoute("/ueber-uns", () => import("./pages/AboutUsPage"), "AboutUsPage"),
    lazyRoute("/en/about-us", () => import("./pages/AboutUsPage"), "AboutUsPage"),
    // Jobs pages
    lazyRoute("/jobs", () => import("./pages/JobsPage"), "JobsPage"),
    lazyRoute("/en/careers", () => import("./pages/JobsPage"), "JobsPage"),
    lazyRoute("/jobs/senior-software-engineer", () => import("./pages/JobSeniorEngineer"), "JobSeniorEngineer"),
    lazyRoute("/en/careers/senior-software-engineer", () => import("./pages/JobSeniorEngineer"), "JobSeniorEngineer"),
    lazyRoute("/jobs/executive-assistant", () => import("./pages/JobExecutiveAssistant"), "JobExecutiveAssistant"),
    lazyRoute(
        "/en/careers/executive-assistant",
        () => import("./pages/JobExecutiveAssistant"),
        "JobExecutiveAssistant"
    ),
    // Data Protection pages
    lazyRoute("/datenschutz", () => import("./pages/DataProtectionPage"), "DataProtectionPage"),
    lazyRoute("/en/privacy", () => import("./pages/DataProtectionPage"), "DataProtectionPage"),
    // Partners pages
    lazyRoute("/partner", () => import("./pages/PartnersPage"), "PartnersPage"),
    lazyRoute("/en/partners", () => import("./pages/PartnersPage"), "PartnersPage"),
    // Terms pages
    lazyRoute("/agb", () => import("./pages/AgbPage"), "AgbPage"),
    lazyRoute("/en/terms", () => import("./pages/AgbPage"), "AgbPage"),
    // Platform routes - German (default)
    // Note: /platform redirects to /plattform (see redirectMapping)
    lazyRoute("/plattform", () => import("./pages/PlatformPage"), "PlatformPage"),
    lazyRoute("/en/platform", () => import("./pages/PlatformPage"), "PlatformPage"),
    // /platform/product + /platform/technology (the old product narrative) retired with the
    // /plattform overhaul (Phase-3 Eckpfeiler 2). All four routes now 301 → /plattform via
    // redirectMapping. The PlatformProduct/PlatformTechnology components stay in the repo for
    // now (final teardown with the legacy-page cleanup, Phase 4).
    // VP-Editionen-Seiten (§8 Hybrid-A, Lösch-Hälfte, Etappe 2) entfernt: Overview,
    // Business, Professional, Starter, Starter-Features, die VP-Branch-Seiten und der
    // Dienstleistungs-Katalog. Jede alte URL 301t single-hop auf ihren Nachfolger
    // (Editionen → Offerten-Generator, Katalog/Branchen → /angebote bzw. /angebote/branche)
    // via redirectMapping (App.tsx erzeugt je Mapping-Key genau eine Redirect-Route).
    // ServiceCatalogPage/-PreviewPage + page-service-catalog bleiben im Repo (unrouted).
    // Offerten-Generator (Phase-3 Etappe 2) — /angebote/ki-anwendungen/offerten-generator.
    // German (default)
    // Intelligente Assistenten (Endgame § 4, Blocker B5): der Katalog-Eintrag auf
    // /angebote braucht eine Live-Detailseite (D9).
    lazyRoute("/angebote/ki-anwendungen/assistenten", () => import("./pages/AssistentenPage"), "AssistentenPage"),
    lazyRoute("/en/offerings/ai-applications/assistants", () => import("./pages/AssistentenPage"), "AssistentenPage"),
    lazyRoute(
        "/angebote/ki-anwendungen/offerten-generator",
        () => import("./pages/OffertenGeneratorPage"),
        "OffertenGeneratorPage"
    ),
    // English — segment-wise EN slug (quote-generator), see Story D2.
    lazyRoute(
        "/en/offerings/ai-applications/quote-generator",
        () => import("./pages/OffertenGeneratorPage"),
        "OffertenGeneratorPage"
    ),
    // Document-as-a-Service (Phase-3 Etappe 3) — /angebote/ki-anwendungen/document-as-a-service.
    // The § 5 Muster-Seite. EN slug is identical (technical term), maps segment-wise.
    lazyRoute("/angebote/ki-anwendungen/document-as-a-service", () => import("./pages/DaasPage"), "DaasPage"),
    lazyRoute("/en/offerings/ai-applications/document-as-a-service", () => import("./pages/DaasPage"), "DaasPage"),
    // KI-Anwendungen overview (Phase-3 Etappe 3.4) — the Eckpfeiler-3 distributor.
    // EN slug identical to the category slug (ai-applications), maps segment-wise.
    lazyRoute("/angebote/ki-anwendungen", () => import("./pages/KiAnwendungenPage"), "KiAnwendungenPage"),
    lazyRoute("/en/offerings/ai-applications", () => import("./pages/KiAnwendungenPage"), "KiAnwendungenPage"),
    // Blog routes (duplicated from above for legacy catch-all ordering)
    lazyRoute("/blog", () => import("./pages/BlogPage"), "BlogPage"),
    lazyRoute("/blog/:slug", () => import("./pages/BlogPostPage"), "BlogPostPage"),
    // Platform 404 catch-all
    lazyRoute("/platform/*", () => import("./pages/NotFound"), "NotFound"),
    lazyRoute("*", () => import("./pages/NotFound"), "NotFound"),
];
